import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Plus, X, ArrowLeftRight, RotateCcw, History, AlertTriangle, Loader, ListFilter, CalendarClock } from 'lucide-react';

// ── Fleet · Vehicle Swaps ─────────────────────────────────────────────────────
// Admin-only management of vehicle custody. Each swap is PERMANENT (the regular
// driver changes — also updates the vehicle's live "Leased To") or TEMPORARY (a
// loaner with an expected return; "Return to regular" restores the assigned
// driver). The history this writes is what lets a fine on a (rego, date) resolve
// to the driver who actually had the vehicle that day.

const ASSIGN_URL = '/.netlify/functions/fleet-assignments-sb';

const api = (action, payload = {}) =>
  fetch(ASSIGN_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
    .then((r) => r.json());

const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)' };
const th = { padding: '8px 10px', textAlign: 'left', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', borderTop: '1px solid var(--hp-border)', fontSize: '.86rem', verticalAlign: 'middle' };

function KindBadge({ kind, onClick }) {
  const temp = kind === 'temporary';
  return (
    <span onClick={onClick} title={onClick ? 'Change swap type' : undefined} role={onClick ? 'button' : undefined} style={{ cursor: onClick ? 'pointer' : 'default', whiteSpace: 'nowrap', display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '.68rem', fontWeight: 700, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.05em', background: temp ? 'rgba(245,158,11,0.16)' : 'rgba(37,99,235,0.16)', color: temp ? 'var(--hp-amber, #f59e0b)' : '#3b82f6' }}>
      {temp ? 'Temporary' : 'Permanent'}{onClick ? ' ▾' : ''}
    </span>
  );
}

export default function FleetSwapsTab({ vehicles, personnel, currentUser, addNotification, onPermanentSwap, onFleetLogins }) {
  const [assignments, setAssignments] = useState([]);
  const [pending, setPending] = useState([]); // future-dated swaps awaiting their day
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null); // { rego, rows, loading }
  const [kindEdit, setKindEdit] = useState(null); // { row, scheduled, kind, expectedReturn, saving }

  // Full swap-history log (all vehicles, open + closed) with client-side filters.
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logLoaded, setLogLoaded] = useState(false);
  const blankFilters = { rego: '', driverLogin: '', kind: '', from: '', to: '' };
  const [filters, setFilters] = useState(blankFilters);
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const blank = { rego: '', kind: 'permanent', driverLogin: '', startDate: todayLocalISO(), expectedReturn: '', note: '' };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const activeVehicles = (vehicles || []).filter((v) => !v.archived && v.rego);
  const activeDrivers = (personnel || []).filter((p) => p.status === 'active');
  const driverName = (login) => (personnel || []).find((p) => p.loginName?.toUpperCase() === (login || '').toUpperCase())?.name || login || '—';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api('listAssignments'), api('listPending')]);
      if (r.success) setAssignments(r.assignments);
      else addNotification?.(`Could not load assignments: ${r.error || 'unknown error'}`, 'error');
      if (p.success) setPending(p.pending || []);
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setLoading(false); }
  }, [addNotification]);

  const cancelPending = async (row) => {
    if (!window.confirm(`Cancel the scheduled swap of ${row.rego} → ${row.driverName || driverName(row.driverLogin)} on ${fmtDate(row.startDate)}?`)) return;
    try {
      const r = await api('cancelPending', { id: row.id });
      if (!r.success) { addNotification?.(`Could not cancel: ${r.error || 'unknown error'}`, 'error'); return; }
      addNotification?.('Scheduled swap cancelled', 'success');
      load();
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
  };

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.rego) { addNotification?.('Pick a vehicle', 'error'); return; }
    if (!form.driverLogin) { addNotification?.('Pick a driver', 'error'); return; }
    if (!form.startDate) { addNotification?.('Set a start date', 'error'); return; }
    if (form.kind === 'temporary' && form.expectedReturn && form.expectedReturn < form.startDate) {
      addNotification?.('Expected return is before the start date', 'error'); return;
    }
    setSaving(true);
    try {
      const dn = driverName(form.driverLogin);
      const r = await api('createSwap', {
        rego: form.rego, kind: form.kind, driverLogin: form.driverLogin, driverName: dn,
        startDate: form.startDate, expectedReturn: form.kind === 'temporary' ? form.expectedReturn : '', note: form.note,
      });
      if (!r.success) { addNotification?.(`Swap failed: ${r.error || 'unknown error'}`, 'error'); return; }
      if (r.scheduled) {
        // Future-dated: parked until its effective day, nothing live has changed yet —
        // do NOT touch the current "Leased To" / portal van.
        addNotification?.(`${form.rego} → ${dn} scheduled for ${fmtDate(r.effectiveDate || form.startDate)} (activates that morning)`, 'success');
      } else {
        if (form.kind === 'permanent') onPermanentSwap?.(form.rego.toUpperCase(), form.driverLogin.toUpperCase(), r.released || []);
        addNotification?.(`${form.rego} ${form.kind === 'permanent' ? 'reassigned to' : 'temporarily swapped to'} ${dn}`, 'success');
      }
      setForm(blank); setShowForm(false); load();
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  const returnToRegular = async (row) => {
    const when = window.prompt(`Return ${row.rego} to its regular driver — actual return date (YYYY-MM-DD):`, todayLocalISO());
    if (!when) return;
    try {
      const r = await api('closeSwap', { id: row.id, returnedAt: when });
      if (!r.success) { addNotification?.(`Return failed: ${r.error || 'unknown error'}`, 'error'); return; }
      addNotification?.(`${row.rego} returned to ${r.restoredLogin ? driverName(r.restoredLogin) : 'its regular driver'}`, 'success');
      load();
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
  };

  // Fix a swap recorded with the wrong type. The server undoes what the old type did
  // (e.g. a permanent swap's new "Leased To" and released van) and applies the new one.
  const openKindEdit = (row, scheduled) => setKindEdit({
    row, scheduled,
    kind: row.kind === 'permanent' ? 'temporary' : 'permanent',
    expectedReturn: row.expectedReturn || '',
    saving: false,
  });

  const saveKind = async () => {
    const { row, kind, expectedReturn, scheduled } = kindEdit;
    if (kind === row.kind) { setKindEdit(null); return; }
    if (kind === 'temporary' && expectedReturn && expectedReturn < row.startDate) {
      addNotification?.('Expected return is before the start date', 'error'); return;
    }
    setKindEdit((k) => ({ ...k, saving: true }));
    try {
      const r = await api('changeKind', { id: row.id, kind, expectedReturn: kind === 'temporary' ? expectedReturn : '' });
      if (!r.success) { addNotification?.(`Could not change type: ${r.error || 'unknown error'}`, 'error'); setKindEdit((k) => ({ ...k, saving: false })); return; }
      if (!scheduled && r.fleetLogins && Object.keys(r.fleetLogins).length) onFleetLogins?.(r.fleetLogins);
      const back = Object.entries(r.fleetLogins || {}).filter(([rego, login]) => rego !== row.rego && login);
      const regular = (r.fleetLogins || {})[row.rego];
      const bits = [];
      if (kind === 'temporary' && !scheduled) {
        bits.push(regular ? `${row.rego} back to ${driverName(regular)}` : `${row.rego} has no regular driver`);
        back.forEach(([rego, login]) => bits.push(`${driverName(login)} back on ${rego}`));
      }
      addNotification?.(`${row.rego} is now a ${kind} swap${bits.length ? ` — ${bits.join(' · ')}` : ''}`, 'success');
      setKindEdit(null); load();
      if (logLoaded) loadLog();
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); setKindEdit((k) => ({ ...k, saving: false })); }
  };

  const openHistory = async (rego) => {
    setHistory({ rego, rows: [], loading: true });
    try {
      const r = await api('listHistory', { rego });
      setHistory({ rego, rows: r.success ? r.history : [], loading: false });
    } catch { setHistory({ rego, rows: [], loading: false }); }
  };

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const r = await api('listAllHistory');
      if (r.success) { setLog(r.history || []); setLogLoaded(true); }
      else addNotification?.(`Could not load swap history: ${r.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setLogLoading(false); }
  }, [addNotification]);

  const toggleLog = () => {
    setShowLog((s) => {
      if (!s && !logLoaded) loadLog();
      return !s;
    });
  };

  // Rego / driver options for the filter dropdowns, from what's actually in the log.
  const logRegos = useMemo(() => [...new Set(log.map((r) => r.rego).filter(Boolean))].sort(), [log]);
  const logDrivers = useMemo(() => {
    const m = new Map();
    log.forEach((r) => { if (r.driverLogin && !m.has(r.driverLogin)) m.set(r.driverLogin, r.driverName || driverName(r.driverLogin)); });
    return [...m.entries()].sort((a, b) => (a[1] > b[1] ? 1 : -1));
  }, [log]);

  const filteredLog = useMemo(() => {
    const f = filters;
    return log.filter((r) => {
      if (f.rego && r.rego !== f.rego) return false;
      if (f.driverLogin && (r.driverLogin || '').toUpperCase() !== f.driverLogin.toUpperCase()) return false;
      if (f.kind && r.kind !== f.kind) return false;
      // Date window: keep a row whose custody interval overlaps [from, to].
      const end = r.returnedAt || r.endDate || r.expectedReturn || '9999-12-31';
      if (f.from && end < f.from) return false;
      if (f.to && r.startDate && r.startDate > f.to) return false;
      return true;
    });
  }, [log, filters]);

  const filtersActive = Object.values(filters).some(Boolean);

  const temps = assignments.filter((a) => a.kind === 'temporary');
  const perms = assignments.filter((a) => a.kind === 'permanent');
  const overdue = temps.filter((a) => a.overdue);

  return (
    <div>
      {/* Header + stats */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <ArrowLeftRight size={18} color="var(--hp-gold)" />
        <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Vehicle Swaps</h3>
        <span style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>
          {perms.length} assigned · {temps.length} on temporary loan{overdue.length ? ` · ${overdue.length} return overdue` : ''}
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          <button className={`btn btn-sm ${showLog ? 'btn-primary' : 'btn-secondary'}`} onClick={toggleLog}>
            <History size={14} /> Swap history
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm((s) => !s)}>
            <Plus size={14} /> New swap
          </button>
        </span>
      </div>

      <p style={{ fontSize: '.82rem', color: 'var(--hp-dim)', margin: '0 0 16px', lineHeight: 1.6 }}>
        A <strong>permanent</strong> swap changes the vehicle's regular driver (and its "Leased&nbsp;To" on the Operations tab).
        A <strong>temporary</strong> swap is a loaner with an expected return — the regular driver is preserved, and
        <strong> Return&nbsp;to&nbsp;regular</strong> restores them. This history is what nominates the right driver for a fine.
        Give a swap a <strong>future date</strong> and it's <strong>scheduled</strong> — nothing changes until it activates that morning.
      </p>

      {/* New swap form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 18, padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 700 }}>Record a swap</div>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowForm(false); setForm(blank); }}><X size={15} /></button>
          </div>
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Vehicle</label>
              <select className="input" style={inputStyle} value={form.rego} onChange={(e) => set('rego', e.target.value)}>
                <option value="">— Select vehicle —</option>
                {activeVehicles.map((v) => (
                  <option key={v.rego} value={v.rego}>{v.rego}{[v.make, v.model].filter(Boolean).length ? ` — ${[v.make, v.model].filter(Boolean).join(' ')}` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Swap type</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['permanent', 'Permanent'], ['temporary', 'Temporary']].map(([k, lbl]) => (
                  <button key={k} type="button" onClick={() => set('kind', k)}
                    className={`btn btn-sm ${form.kind === k ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }}>{lbl}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={labelStyle}>{form.kind === 'temporary' ? 'Temporary driver' : 'New regular driver'}</label>
              <select className="input" style={inputStyle} value={form.driverLogin} onChange={(e) => set('driverLogin', e.target.value)}>
                <option value="">— Select driver —</option>
                {activeDrivers.map((d) => (
                  <option key={d.loginName} value={d.loginName}>{d.loginName} — {d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{form.kind === 'temporary' ? 'Loan starts' : 'Effective date'}</label>
              <input type="date" className="input" style={inputStyle} value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            {form.kind === 'temporary' && (
              <div>
                <label style={labelStyle}>Expected return</label>
                <input type="date" className="input" style={inputStyle} value={form.expectedReturn} onChange={(e) => set('expectedReturn', e.target.value)} />
              </div>
            )}
            <div style={{ gridColumn: form.kind === 'temporary' ? 'auto' : '1 / -1' }}>
              <label style={labelStyle}>Note (optional)</label>
              <input className="input" style={{ ...inputStyle, width: '100%' }} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="e.g. regular van in for service" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={submit} disabled={saving}>
              {saving ? <><Loader size={14} /> Saving…</> : <><ArrowLeftRight size={14} /> Record swap</>}
            </button>
            <button className="btn btn-secondary" onClick={() => { setShowForm(false); setForm(blank); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Overdue banner */}
      {overdue.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '.85rem' }}>
          <AlertTriangle size={15} color="var(--hp-red, #ef4444)" />
          <span><strong>{overdue.length}</strong> temporary swap{overdue.length > 1 ? 's are' : ' is'} past the expected return date — confirm the vehicle{overdue.length > 1 ? 's are' : ' is'} back or extend the loan.</span>
        </div>
      )}

      {/* Scheduled (future-dated) swaps — parked until their effective day */}
      {pending.length > 0 && (
        <div className="card" style={{ marginBottom: 14, padding: 0, overflow: 'hidden', border: '1px solid rgba(245,158,11,0.4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', borderBottom: '1px solid var(--hp-border)', background: 'rgba(245,158,11,0.06)' }}>
            <CalendarClock size={15} color="var(--hp-amber, #f59e0b)" />
            <strong style={{ fontSize: '.92rem' }}>Scheduled swaps</strong>
            <span style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>{pending.length} waiting to activate on their date — nothing changes until then</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={th}>Vehicle</th><th style={th}>Type</th><th style={th}>Driver</th>
                  <th style={th}>Activates</th><th style={th}>Note</th><th style={{ ...th, textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => (
                  <tr key={p.id}>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{p.rego}</td>
                    <td style={td}><KindBadge kind={p.kind} onClick={() => openKindEdit(p, true)} /></td>
                    <td style={td}>{p.driverName || driverName(p.driverLogin)}{p.driverLogin ? <span style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}> · {p.driverLogin}</span> : ''}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmtDate(p.startDate)}</td>
                    <td style={{ ...td, color: 'var(--hp-dim)', fontSize: '.8rem', maxWidth: 220 }}>{p.note || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => cancelPending(p)}><X size={13} /> Cancel</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Current assignments table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 24, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading assignments…</div>
        ) : assignments.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--hp-dim)' }}>No open assignments yet. Record a swap to start the custody history for a vehicle.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={th}>Vehicle</th>
                  <th style={th}>Type</th>
                  <th style={th}>Driver</th>
                  <th style={th}>Since</th>
                  <th style={th}>Expected return</th>
                  <th style={{ ...th, textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {assignments.slice().sort((a, b) => (a.rego > b.rego ? 1 : -1)).map((a) => (
                  <tr key={a.id} style={{ background: a.overdue ? 'rgba(239,68,68,0.05)' : 'transparent' }}>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{a.rego}</td>
                    <td style={td}><KindBadge kind={a.kind} onClick={() => openKindEdit(a, false)} /></td>
                    <td style={td}>{a.driverName || driverName(a.driverLogin)}{a.driverLogin ? <span style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}> · {a.driverLogin}</span> : ''}</td>
                    <td style={td}>{fmtDate(a.startDate)}</td>
                    <td style={td}>
                      {a.kind === 'temporary'
                        ? (a.expectedReturn
                            ? <span style={{ color: a.overdue ? 'var(--hp-red, #ef4444)' : 'inherit', fontWeight: a.overdue ? 700 : 400 }}>{fmtDate(a.expectedReturn)}{a.overdue ? ' · overdue' : ''}</span>
                            : <span style={{ color: 'var(--hp-dim)' }}>open</span>)
                        : <span style={{ color: 'var(--hp-dim)' }}>—</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost btn-sm" title="Vehicle history" onClick={() => openHistory(a.rego)}><History size={14} /></button>
                      {a.kind === 'temporary' && (
                        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 6 }} onClick={() => returnToRegular(a)}>
                          <RotateCcw size={13} /> Return to regular
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Full swap history — filterable log across every vehicle */}
      {showLog && (
        <div className="card" style={{ marginTop: 18, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--hp-border)', flexWrap: 'wrap' }}>
            <History size={16} color="var(--hp-gold)" />
            <strong style={{ fontSize: '.95rem' }}>Swap history</strong>
            <span style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>
              {logLoading ? 'loading…' : `${filteredLog.length}${filtersActive ? ` of ${log.length}` : ''} swap${filteredLog.length === 1 ? '' : 's'}`}
            </span>
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={loadLog} title="Refresh history" disabled={logLoading}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          {/* Filters: driver · registration · date window · type */}
          <div style={{ display: 'flex', gap: 12, padding: '14px 16px', flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--hp-surface)', borderBottom: '1px solid var(--hp-border)' }}>
            <div style={{ minWidth: 150 }}>
              <label style={labelStyle}><ListFilter size={11} style={{ verticalAlign: '-1px' }} /> Driver</label>
              <select className="input" style={{ ...inputStyle, width: '100%' }} value={filters.driverLogin} onChange={(e) => setFilter('driverLogin', e.target.value)}>
                <option value="">All drivers</option>
                {logDrivers.map(([login, name]) => (<option key={login} value={login}>{name} — {login}</option>))}
              </select>
            </div>
            <div style={{ minWidth: 130 }}>
              <label style={labelStyle}>Registration</label>
              <select className="input" style={{ ...inputStyle, width: '100%' }} value={filters.rego} onChange={(e) => setFilter('rego', e.target.value)}>
                <option value="">All vehicles</option>
                {logRegos.map((rego) => (<option key={rego} value={rego}>{rego}</option>))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>From</label>
              <input type="date" className="input" style={inputStyle} value={filters.from} onChange={(e) => setFilter('from', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>To</label>
              <input type="date" className="input" style={inputStyle} value={filters.to} onChange={(e) => setFilter('to', e.target.value)} />
            </div>
            <div style={{ minWidth: 130 }}>
              <label style={labelStyle}>Type</label>
              <select className="input" style={{ ...inputStyle, width: '100%' }} value={filters.kind} onChange={(e) => setFilter('kind', e.target.value)}>
                <option value="">All types</option>
                <option value="permanent">Permanent</option>
                <option value="temporary">Temporary</option>
              </select>
            </div>
            {filtersActive && (
              <button className="btn btn-ghost btn-sm" onClick={() => setFilters(blankFilters)}><X size={13} /> Clear filters</button>
            )}
          </div>

          {logLoading ? (
            <div style={{ padding: 24, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading swap history…</div>
          ) : filteredLog.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--hp-dim)' }}>
              {log.length === 0 ? 'No swaps recorded yet.' : 'No swaps match these filters.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                <thead>
                  <tr>
                    <th style={th}>Vehicle</th>
                    <th style={th}>Type</th>
                    <th style={th}>Driver</th>
                    <th style={th}>From</th>
                    <th style={th}>To</th>
                    <th style={th}>Status</th>
                    <th style={th}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLog.map((r) => (
                    <tr key={r.id} style={{ background: r.overdue ? 'rgba(239,68,68,0.05)' : 'transparent' }}>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{r.rego}</td>
                      <td style={td}><KindBadge kind={r.kind} /></td>
                      <td style={td}>{r.driverName || driverName(r.driverLogin) || '—'}{r.driverLogin ? <span style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}> · {r.driverLogin}</span> : ''}</td>
                      <td style={td}>{fmtDate(r.startDate)}</td>
                      <td style={td}>{r.returnedAt ? fmtDate(r.returnedAt) : r.endDate ? fmtDate(r.endDate) : (r.kind === 'temporary' && r.expectedReturn ? <span style={{ color: r.overdue ? 'var(--hp-red, #ef4444)' : 'var(--hp-dim)' }}>{`exp. ${fmtDate(r.expectedReturn)}`}{r.overdue ? ' · overdue' : ''}</span> : <span style={{ color: 'var(--hp-dim)' }}>ongoing</span>)}</td>
                      <td style={{ ...td, color: r.status === 'open' ? 'var(--hp-green, #22c55e)' : 'var(--hp-dim)' }}>{r.status}</td>
                      <td style={{ ...td, color: 'var(--hp-dim)', fontSize: '.8rem', maxWidth: 220 }}>{r.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Change swap type */}
      {kindEdit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }} onClick={() => !kindEdit.saving && setKindEdit(null)}>
          <div className="card" style={{ width: 460, maxWidth: '100%', padding: 22 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Swap type — <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{kindEdit.row.rego}</span></h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setKindEdit(null)} disabled={kindEdit.saving}><X size={16} /></button>
            </div>
            <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)', marginBottom: 14 }}>
              {kindEdit.row.driverName || driverName(kindEdit.row.driverLogin)} · {kindEdit.scheduled ? 'activates' : 'since'} {fmtDate(kindEdit.row.startDate)}
            </div>
            <label style={labelStyle}>Swap type</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[['permanent', 'Permanent'], ['temporary', 'Temporary']].map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => setKindEdit((e) => ({ ...e, kind: k }))}
                  className={`btn btn-sm ${kindEdit.kind === k ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }}>{lbl}</button>
              ))}
            </div>
            {kindEdit.kind === 'temporary' && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Expected return</label>
                <input type="date" className="input" style={inputStyle} value={kindEdit.expectedReturn} onChange={(e) => setKindEdit((k) => ({ ...k, expectedReturn: e.target.value }))} />
              </div>
            )}
            {kindEdit.kind !== kindEdit.row.kind && !kindEdit.scheduled && (
              <p style={{ fontSize: '.8rem', color: 'var(--hp-dim)', lineHeight: 1.55, margin: '0 0 14px' }}>
                {kindEdit.kind === 'temporary'
                  ? <>{kindEdit.row.rego} goes back to its previous regular driver, and {kindEdit.row.driverName || driverName(kindEdit.row.driverLogin)} is put back on their own van if nobody has taken it since. They keep {kindEdit.row.rego} on their portal until it's returned.</>
                  : <>{kindEdit.row.driverName || driverName(kindEdit.row.driverLogin)} becomes {kindEdit.row.rego}'s regular driver from {fmtDate(kindEdit.row.startDate)}, and {(() => {
                      const held = (vehicles || []).filter((v) => v.rego !== kindEdit.row.rego && (v.login || '').toUpperCase() === (kindEdit.row.driverLogin || '').toUpperCase()).map((v) => v.rego);
                      return held.length ? <>their current van <strong>{held.join(', ')}</strong> is released.</> : 'any van they held before is released.';
                    })()}</>}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={saveKind} disabled={kindEdit.saving || kindEdit.kind === kindEdit.row.kind}>
                {kindEdit.saving ? <><Loader size={14} /> Saving…</> : 'Save'}
              </button>
              <button className="btn btn-secondary" onClick={() => setKindEdit(null)} disabled={kindEdit.saving}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* History modal */}
      {history && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }} onClick={() => setHistory(null)}>
          <div className="card" style={{ width: 640, maxWidth: '100%', maxHeight: '82vh', overflowY: 'auto', padding: 22 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Custody history — <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{history.rego}</span></h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setHistory(null)}><X size={16} /></button>
            </div>
            {history.loading ? (
              <div style={{ color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
            ) : history.rows.length === 0 ? (
              <div style={{ color: 'var(--hp-dim)' }}>No recorded history for this vehicle yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Type</th><th style={th}>Driver</th><th style={th}>From</th><th style={th}>To</th><th style={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.rows.map((r) => (
                    <tr key={r.id}>
                      <td style={td}><KindBadge kind={r.kind} /></td>
                      <td style={td}>{r.driverName || driverName(r.driverLogin) || '—'}</td>
                      <td style={td}>{fmtDate(r.startDate)}</td>
                      <td style={td}>{r.returnedAt ? fmtDate(r.returnedAt) : r.endDate ? fmtDate(r.endDate) : (r.kind === 'temporary' && r.expectedReturn ? `exp. ${fmtDate(r.expectedReturn)}` : 'ongoing')}</td>
                      <td style={{ ...td, color: r.status === 'open' ? 'var(--hp-green, #22c55e)' : 'var(--hp-dim)' }}>{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
